import { API_BASE } from "./common";

const SILENT_REFRESH_TIMEOUT = 5000;

/**
 * Attempts to silently refresh the Cloudflare Access session using a hidden iframe.
 * If the user has an active session with the Identity Provider, this will succeed
 * and set a new cookie. If the IdP session is expired or requires interaction,
 * this will likely timeout or fail (due to X-Frame-Options).
 */
export const attemptSilentRefresh = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";

    // Determine the login URL
    let loginUrl: string;
    try {
      // If API_BASE is a full URL, we want to use its origin
      const apiOrigin = new URL(API_BASE).origin;
      loginUrl = new URL("/cdn-cgi/access/login", apiOrigin).toString();
    } catch {
      // If API_BASE is relative (e.g. "/api"), use the current origin
      loginUrl = "/cdn-cgi/access/login";
    }

    iframe.src = loginUrl;

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      console.warn("Silent refresh timed out");
      resolve(false);
    }, SILENT_REFRESH_TIMEOUT);

    iframe.onload = () => {
      clearTimeout(timeout);
      cleanup();
      resolve(true);
    };

    // Note: onerror often doesn't fire for cross-origin or 401/403/frame-blocked errors,
    // so we rely heavily on the timeout.
    iframe.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      console.error("Silent refresh iframe error");
      resolve(false);
    };

    document.body.appendChild(iframe);
  });
};
